/**
 * Cart-side guard for configured-product builds (Name Clicker, Bag Tag).
 *
 * A build is one R0 configured line plus priced charge lines that share its `_pw_config_id`. After
 * every cart change (and on load when the cart holds builds) this module reads the cart back from
 * Shopify, assesses each build against its own stored configuration and, when a build is
 * inconsistent, FAILS CLOSED:
 *   - the problem is shown on the build's line with one recovery action, "Remove this build";
 *   - the cart's checkout buttons are disabled with an explanation.
 * It never restores, re-adds or edits paid quantities on its own. The only cart change it makes is
 * the explicit whole-build removal the customer asks for (also used for the configured line's own
 * remove button, so a build is always removed as a whole and never leaves orphaned charges).
 *
 * This is a usability layer, not a security boundary: the server-side block is Shopify's Cart and
 * Checkout Validation Function (see docs/07 §12 for its status on this store's plan).
 *
 * Separate from the clicker helper-line reconciliation in assets/pw-cart.js, which is untouched.
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { assessBuilds, cartHasBuilds, formatZar, removalUpdatesForBuild } from '@theme/pw-config-core';

const SOURCE = 'pw-config-guard';
const FLAG = 'pw-config-builds';
const CHECKOUT_MESSAGE = 'A build in your cart is incomplete. Remove it below (you can build it again) to continue to checkout.';

let busy = false;
let queued = false;
let resolversPromise = null;
let lastAssessment = [];
let rendering = false;
let renderTimer = 0;

const routes = () => {
  const r = typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
  return { cart: r.cart_url || '/cart', update: r.cart_update_url || '/cart/update.js' };
};

/** The schema modules are only fetched once a cart actually holds a build. */
function loadResolvers() {
  if (!resolversPromise) {
    resolversPromise = Promise.all([import('@theme/pw-name-clicker-schema'), import('@theme/pw-bag-tag-schema')]).then(
      ([nameClicker, bagTag]) => ({ [nameClicker.SCHEMA_ID]: nameClicker.resolver, [bagTag.SCHEMA_ID]: bagTag.resolver })
    );
  }
  return resolversPromise;
}

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');

const fetchCart = () => fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

/* ------------------------------------------------------------------------------------------ assess */

async function assess() {
  if (busy) {
    queued = true;
    return;
  }
  busy = true;
  try {
    const cart = await fetchCart();
    const hasBuilds = cartHasBuilds(cart.items);
    try {
      if (hasBuilds) sessionStorage.setItem(FLAG, '1');
      else sessionStorage.removeItem(FLAG);
    } catch (_) {}
    lastAssessment = hasBuilds ? assessBuilds(cart.items, await loadResolvers()) : [];
    render();
  } catch (_) {
    // Never let the guard itself break the cart the customer is using; the server-side validation remains.
  } finally {
    busy = false;
    if (queued) {
      queued = false;
      assess();
    }
  }
}

/* ------------------------------------------------------------------------------------------ render */

function noticeFor(assessment) {
  const notice = document.createElement('div');
  notice.className = 'pw-config-guard__notice';
  notice.setAttribute('role', 'alert');
  const title = document.createElement('p');
  title.className = 'pw-config-guard__title';
  title.textContent = assessment.bare
    ? assessment.component
      ? 'This item is part of a build and cannot be checked out on its own.'
      : 'This product has to be built in the builder and cannot be checked out like this.'
    : assessment.main
      ? 'This build is incomplete and cannot be checked out.'
      : 'These charges belong to a build that is no longer in your cart.';
  const list = document.createElement('ul');
  list.className = 'pw-config-guard__list';
  for (const problem of assessment.problems) {
    const item = document.createElement('li');
    item.textContent = problem;
    list.append(item);
  }
  const help = document.createElement('p');
  help.className = 'pw-config-guard__help';
  help.textContent = assessment.bare
    ? assessment.component
      ? 'Remove it, then build the product in its builder; the builder adds every part at the right price.'
      : 'Remove it, then open the product page and use the builder.'
    : assessment.main
      ? `Remove it and build it again; the builder will add it back at the right price${assessment.totalCents != null ? ` (${formatZar(assessment.totalCents)})` : ''}.`
      : 'Remove these lines to continue.';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button button-secondary pw-config-guard__remove';
  button.dataset.pwRemoveBuild = assessment.configId;
  button.textContent = assessment.bare ? 'Remove it' : assessment.main ? 'Remove this build' : 'Remove these lines';
  notice.append(title, list, help, button);
  return notice;
}

function render() {
  rendering = true;
  try {
    const byId = new Map(lastAssessment.map((assessment) => [assessment.configId, assessment]));
    const shown = new Set();
    for (const placeholder of document.querySelectorAll('[data-pw-guard]')) {
      const assessment = byId.get(placeholder.dataset.pwGuard);
      const isMainRow = placeholder.dataset.pwGuardRole === 'configured';
      const showHere = assessment && !assessment.ok && !shown.has(assessment.configId) && (isMainRow || !assessment.main);
      if (showHere) {
        placeholder.replaceChildren(noticeFor(assessment));
        placeholder.hidden = false;
        shown.add(assessment.configId);
      } else {
        placeholder.replaceChildren();
        placeholder.hidden = true;
      }
    }

    const invalid = lastAssessment.some((assessment) => !assessment.ok);
    for (const button of document.querySelectorAll('#checkout, button[name="checkout"]')) {
      if (invalid) {
        button.disabled = true;
        button.setAttribute('data-pw-guard-disabled', '');
      } else if (button.hasAttribute('data-pw-guard-disabled')) {
        button.disabled = false;
        button.removeAttribute('data-pw-guard-disabled');
      }
    }
    for (const container of document.querySelectorAll('.additional-checkout-buttons')) {
      container.hidden = invalid;
    }
    for (const notice of document.querySelectorAll('[data-pw-guard-checkout]')) {
      notice.textContent = invalid ? CHECKOUT_MESSAGE : '';
      notice.hidden = !invalid;
    }
    document.documentElement.toggleAttribute('data-pw-builds-invalid', invalid);
  } finally {
    rendering = false;
  }
}

// Horizon re-renders the cart sections after every change (Section Rendering morph), which replaces
// the placeholders; re-apply the cached assessment whenever that happens.
new MutationObserver(() => {
  if (rendering || !lastAssessment.length) return;
  clearTimeout(renderTimer);
  renderTimer = setTimeout(render, 40);
}).observe(document.documentElement, { childList: true, subtree: true });

/* ------------------------------------------------------------------------------------------ remove */

async function removeBuild(configId, button) {
  if (!configId) return;
  button.disabled = true;
  const label = button.textContent;
  button.textContent = 'Removing…';
  try {
    const current = await fetchCart();
    // "bare:<line key>" removes a configured product that was added without a build; otherwise every line of the build.
    const updates = configId.startsWith('bare:')
      ? Object.fromEntries(current.items.filter((item) => item.key === configId.slice(5)).map((item) => [item.key, 0]))
      : removalUpdatesForBuild(current.items, configId);
    if (!Object.keys(updates).length) {
      lastAssessment = lastAssessment.filter((assessment) => assessment.configId !== configId);
      render();
      return;
    }
    const result = await fetch(routes().update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sectionIds(), sections_url: window.location.pathname }),
    }).then((r) => r.json());
    if (result.status || result.errors) throw new Error(result.description || 'Could not remove the build');

    // Re-render the drawer, cart page and header exactly as for any other cart change; our own
    // listener then re-assesses what is left.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: [], promise: deferred.promise }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
      detail: { items: result.items, sections: result.sections, source: SOURCE, sourceId: SOURCE, didError: false },
    });
  } catch (_) {
    button.disabled = false;
    button.textContent = label;
    button.insertAdjacentText('afterend', ' Could not remove it. Please try again.');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-pw-remove-build]') : null;
  if (!button) return;
  event.preventDefault();
  removeBuild(button.dataset.pwRemoveBuild, button);
});

/* ------------------------------------------------------------------------------------------ events */

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settle = (detail) => {
    if (detail?.didError) return;
    const items = detail?.items;
    if (Array.isArray(items) && !cartHasBuilds(items)) {
      let flagged = false;
      try {
        flagged = sessionStorage.getItem(FLAG) === '1';
      } catch (_) {}
      if (!flagged) {
        lastAssessment = [];
        render();
        return;
      }
    }
    assess();
  };
  if (event.promise) event.promise.then(({ detail }) => settle(detail)).catch(() => {});
  else settle(undefined);
});

// Carts that already hold builds (older sessions, a tab closed mid-change, the cart page itself). The drawer renders with
// the page, so a build line anywhere in it is seen on any page: a returning customer's checkout button is guarded too.
{
  let flagged = false;
  try {
    flagged = sessionStorage.getItem(FLAG) === '1';
  } catch (_) {}
  const onCartPage = window.location.pathname.replace(/\/$/, '').endsWith('/cart');
  if (flagged || onCartPage || document.querySelector('[data-pw-guard]')) {
    const run = () => assess();
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 600);
  }
}
